import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Button } from '../../../../core/components/atoms/Button';
import { TextArea } from '../../../../core/components/atoms/Textarea';
import {
  MultipleRadioGroup,
  RadioItem,
} from '../../../../core/components/molecules/MultipleRadioGroup';
import { Modal } from '../../../../core/components/templates/Modal';
import { DropCourseFAQTab } from './FAQTab';

interface DropRegularCourseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onDrop: (reasons?: string[]) => void;
  isLoading: boolean;
  courseName: string;
}

function DropRegularCourseModal({
  isOpen,
  onClose,
  courseName,
  onDrop,
}: DropRegularCourseModalProps) {
  const [currentTab, setCurrentTab] = useState<'form' | 'faq'>('form');

  const {
    register,
    control,
    watch,
    handleSubmit,
    setValue,
    trigger,
    formState,
    reset,
  } = useForm();

  function onSubmit(data: any) {
    const reasons = data.reasons.map((reason: RadioItem) => {
      if (reason.value === 'Outro' && data.otherReason) {
        return data.otherReason;
      }
      return reason.value;
    });

    onClose();
    onDrop(reasons);
  }

  const wReasons = watch('reasons');
  const showOtherReason =
    wReasons && wReasons.some((r: RadioItem) => r.value === 'Outro');

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        reset();
        onClose();
      }}
      title='Abandonar curso regular'
      subtitle={courseName}
      noPadding
    >
      <form onSubmit={handleSubmit(onSubmit)} className='flex flex-col gap-xl '>
        {currentTab === 'form' && (
          <div className='flex flex-col gap-lg p-4'>
            <Controller
              name='reasons'
              control={control}
              rules={{
                required: {
                  value: true,
                  message: 'Selecione uma ou mais opções.',
                },
              }}
              render={({ field, fieldState }) => (
                <MultipleRadioGroup
                  label='* Por qual motivo você está abandonando seu curso regular?'
                  value={field.value}
                  errorMessage={fieldState.error?.message}
                  onChange={(value) => {
                    setValue('reasons', value);

                    trigger('reasons');
                  }}
                  items={[
                    {
                      label: 'Estou atrasado(a)',
                      value: 'Estou atrasado(a)',
                    },
                    {
                      label:
                        'Quero um curso com outro foco (Enem, medicina etc)',
                      value:
                        'Quero um curso com outro foco (Enem, medicina etc)',
                    },
                    {
                      label: 'Procuro um curso mais aprofundado',
                      value: 'Procuro um curso mais aprofundado',
                    },
                    {
                      label: 'Achei o curso muito pesado',
                      value: 'Achei o curso muito pesado',
                    },
                    {
                      label: 'Outro',
                      value: 'Outro',
                    },
                  ]}
                />
              )}
            />

            {showOtherReason && (
              <TextArea
                label='Descreva o motivo brevemente em um tweet'
                maxLength={280}
                errorMessage={
                  formState.errors.otherReason && 'Digite um motivo.'
                }
                {...register('otherReason', {
                  required: {
                    value: showOtherReason,
                    message: 'Campo obrigatório.',
                  },
                })}
              />
            )}
          </div>
        )}

        {currentTab === 'faq' && <DropCourseFAQTab />}

        <div className='w-full border-t-hairline border-t-mono-color-lightest'></div>
        {currentTab === 'form' && (
          <footer className='flex w-full gap-lg p-4 pt-0'>
            <Button type='button' variant='outlined' full onClick={onClose}>
              Cancelar
            </Button>
            <Button
              type='button'
              variant='contained'
              full
              onClick={async () => {
                if (await trigger()) {
                  setCurrentTab('faq');
                }
              }}
            >
              Continuar
            </Button>
          </footer>
        )}

        {currentTab === 'faq' && (
          <footer className='flex w-full gap-lg p-4 pt-0'>
            <Button
              type='button'
              variant='outlined'
              LeftIcon={'chevron_left'}
              onClick={() => {
                setCurrentTab('form');
              }}
            >
              Voltar
            </Button>
            <Button variant='contained' full type='submit'>
              Abandonar curso
            </Button>
          </footer>
        )}
      </form>
    </Modal>
  );
}

export { DropRegularCourseModal };
